<script setup>
import { ref, onMounted } from 'vue';
import { trackArticleView } from '../composables/useArticleTracking.js';

const props = defineProps({
  articles: {
    type: Array,
    default: () => [],
  },
  openArticleSlug: {
    type: String,
    default: null,
  },
});

const openArticleId = ref(null);

const toggleArticle = articleId => {
  const isExpanding = openArticleId.value !== articleId;
  openArticleId.value = isExpanding ? articleId : null;

  if (isExpanding) {
    const article = props.articles.find(item => item.article.id === articleId);
    if (article && window.portalConfig?.portalSlug) {
      trackArticleView(window.portalConfig.portalSlug, article.article.slug);
    }
  }
};

const isOpen = articleId => openArticleId.value === articleId;

onMounted(() => {
  if (props.openArticleSlug) {
    const article = props.articles.find(
      item => item.article.slug === props.openArticleSlug
    );
    if (article) {
      openArticleId.value = article.article.id;

      if (window.portalConfig?.portalSlug) {
        trackArticleView(window.portalConfig.portalSlug, article.article.slug);
      }
    }
  }
});
</script>

<template>
  <div class="flex flex-col">
    <div
      v-for="(item, index) in articles"
      :key="item.article.id"
      class="bg-white dark:bg-slate-900 transition-shadow"
      :class="{
        'border-b border-solid border-slate-200 dark:border-slate-700':
          index < articles.length - 1,
      }"
    >
      <!-- Accordion Header -->
      <button
        class="w-full p-6 flex items-center justify-between gap-4 cursor-pointer text-left"
        @click="toggleArticle(item.article.id)"
      >
        <h3
          class="text-base font-semibold text-slate-900 dark:text-slate-50 flex-grow"
        >
          {{ item.article.title }}
        </h3>
        <svg
          class="w-5 h-5 flex-shrink-0 text-slate-600 dark:text-slate-400 transition-transform duration-200"
          :class="{ 'rotate-180': isOpen(item.article.id) }"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <!-- Accordion Content -->
      <div
        v-if="isOpen(item.article.id)"
        v-dompurify-html="item.parsed_content"
        class="px-6 pb-6 prose prose-slate dark:prose-invert max-w-none prose-headings:text-slate-900 dark:prose-headings:text-slate-50 prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-a:text-woot-600 dark:prose-a:text-woot-500 prose-code:text-slate-900 dark:prose-code:text-slate-50 prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-pre:bg-slate-900 dark:prose-pre:bg-slate-950"
      />
    </div>
  </div>
</template>
